import Phaser from 'phaser'
import {
	clearPersistentBudgetState,
	clearPersistentPlayTimeState,
	clearPersistentRankingRunSession,
	getPersistentRankingRunSession,
	setPersistentRankingRunSession,
	setLogoOverlayVisible
} from './EventCenter'
import RankingApiClient, { type DailyRankingRow } from '../services/RankingApiClient'
import { isLocalSessionId, enqueueFinishOperation } from '../services/OfflineRankingQueue'

const SCENE_KEY = 'daily-ranking'
const DEFAULT_RANKING_API_BASE_URL = '/ranking/api/v1'
const DEFAULT_NICKNAME = 'SLAYER'
const TOP_RANKING_LIMIT = 5
const VICTORY_BACKGROUND_KEY = 'ranking-victory-background'
const VICTORY_BACKGROUND_PATH = 'assets/ranking.jpg'
const GAME_OVER_BACKGROUND_KEY = 'ranking-gameover-background'
const GAME_OVER_BACKGROUND_PATH = 'assets/ranking-gameover.jpg'
const HIGHLIGHT_ROW_KEY = 'ranking-highlight-row'
const HIGHLIGHT_ROW_PATH = 'assets/destacado_ranking.png'
const RANKING_SEPARATOR_KEY = 'ranking-separator'
const RANKING_SEPARATOR_PATH = 'assets/separador.png'
const REFERENCE_BG_WIDTH = 1365
const REFERENCE_BG_HEIGHT = 768
const TABLE_LEFT_RATIO = 0.337
const TABLE_TOP_RATIO = 0.58
const TABLE_WIDTH_RATIO = 0.326
const ROW_HEIGHT_RATIO = 0.062
const SEPARATOR_Y_OFFSET = 26
const RANKING_SEPARATOR_WIDTH = 612
const RANKING_SEPARATOR_HEIGHT = 10
const RANKING_ROW_FONT_SIZE_PX = 23
const RANKING_AUTO_RESTART_DELAY_MS = 15000
const MENU_BACKGROUND_MUSIC_KEY = 'music-menu-fondo'
const MENU_BACKGROUND_MUSIC_PATH = 'assets/musica/fondo.mp3'
const MENU_BACKGROUND_MUSIC_VOLUME = 0.18
const PIXEL_FONT_FAMILY = '"Press Start 2P", system-ui'

type RankingSummary = {
	nickname: string
	finalBudget: number
	finalMaxBudget: number
	elapsedMs: number
}

type RankingRowView = {
	position: number
	nickname: string
	score: number
	isCurrentPlayer: boolean
}

type RankingRowTexts = {
	row: RankingRowView
	highlightBg?: Phaser.GameObjects.Image
	positionText: Phaser.GameObjects.Text
	separatorImage: Phaser.GameObjects.Image
	scoreText: Phaser.GameObjects.Text
	nicknameText: Phaser.GameObjects.Text
}

export default class DailyRanking extends Phaser.Scene
{
	private background?: Phaser.GameObjects.Image
	private rankingApi?: RankingApiClient
	private titleText?: Phaser.GameObjects.Text
	private statusText?: Phaser.GameObjects.Text
	private rowTexts: RankingRowTexts[] = []
	private autoRestartEvent?: Phaser.Time.TimerEvent
	private resizeHandler?: (gameSize: Phaser.Structs.Size) => void
	private backgroundMusic?: Phaser.Sound.BaseSound
	private rankingAvailable = true
	private summary: RankingSummary = {
		nickname: DEFAULT_NICKNAME,
		finalBudget: 0,
		finalMaxBudget: 3000,
		elapsedMs: 0
	}
	private endReason: 'finished_level' | 'game_over' = 'finished_level'

	constructor()
	{
		super(SCENE_KEY)
	}

	init(data: { summary?: Partial<RankingSummary>, endReason?: 'finished_level' | 'game_over' } = {})
	{
		const fallbackNickname = this.game.registry.get('playerNickname')
		this.summary = {
			nickname: typeof data.summary?.nickname === 'string' && data.summary.nickname.trim().length > 0
				? data.summary.nickname.trim().toUpperCase()
				: (typeof fallbackNickname === 'string' && fallbackNickname.trim().length > 0
					? fallbackNickname.trim().toUpperCase()
					: DEFAULT_NICKNAME),
			finalBudget: Math.max(0, Math.round(data.summary?.finalBudget ?? 0)),
			finalMaxBudget: Math.max(0, Math.round(data.summary?.finalMaxBudget ?? 3000)),
			elapsedMs: Math.max(0, Math.floor(data.summary?.elapsedMs ?? 0))
		}

		this.endReason = data.endReason === 'game_over' ? 'game_over' : 'finished_level'
	}

	preload()
	{
		if (!this.textures.exists(VICTORY_BACKGROUND_KEY))
		{
			this.load.image(VICTORY_BACKGROUND_KEY, VICTORY_BACKGROUND_PATH)
		}

		if (!this.textures.exists(GAME_OVER_BACKGROUND_KEY))
		{
			this.load.image(GAME_OVER_BACKGROUND_KEY, GAME_OVER_BACKGROUND_PATH)
		}

		if (!this.textures.exists(HIGHLIGHT_ROW_KEY))
		{
			this.load.image(HIGHLIGHT_ROW_KEY, HIGHLIGHT_ROW_PATH)
		}

		if (!this.textures.exists(RANKING_SEPARATOR_KEY))
		{
			this.load.image(RANKING_SEPARATOR_KEY, RANKING_SEPARATOR_PATH)
		}

		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			this.load.audio(MENU_BACKGROUND_MUSIC_KEY, MENU_BACKGROUND_MUSIC_PATH)
		}
	}

	create()
	{
		setLogoOverlayVisible(false)
		if (this.scene.isActive('logo-overlay'))
		{
			this.scene.stop('logo-overlay')
		}

		const apiBaseUrlFromRegistry = this.game.registry.get('rankingApiBaseUrl')
		this.rankingApi = new RankingApiClient(typeof apiBaseUrlFromRegistry === 'string'
			? apiBaseUrlFromRegistry
			: DEFAULT_RANKING_API_BASE_URL)

		this.background = this.add.image(0, 0, this.getBackgroundTextureKey()).setOrigin(0)
		this.startBackgroundMusic()

		this.statusText = this.add.text(this.scale.width * 0.5, this.scale.height * 0.28, 'Cargando ranking diario...', {
			fontFamily: PIXEL_FONT_FAMILY,
			fontSize: '16px',
			color: '#011145',
			align: 'center'
		}).setOrigin(0.5).setDepth(20)

		this.layout()
		this.autoRestartEvent = this.time.delayedCall(RANKING_AUTO_RESTART_DELAY_MS, () => {
			this.restartGame()
		})

		this.resizeHandler = () => {
			this.layout()
		}
		this.scale.on(Phaser.Scale.Events.RESIZE, this.resizeHandler)

		this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
			this.stopBackgroundMusic()
			if (this.resizeHandler)
			{
				this.scale.off(Phaser.Scale.Events.RESIZE, this.resizeHandler)
				this.resizeHandler = undefined
			}

			if (this.autoRestartEvent)
			{
				this.autoRestartEvent.remove(false)
				this.autoRestartEvent = undefined
			}
		})

		void this.finishRunAndLoadRanking()
	}

	private startBackgroundMusic()
	{
		if (this.backgroundMusic?.isPlaying)
		{
			return
		}

		if (!this.cache.audio.exists(MENU_BACKGROUND_MUSIC_KEY))
		{
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			this.backgroundMusic = existingMusic
			if (!existingMusic.isPlaying)
			{
				existingMusic.play({ loop: true, volume: MENU_BACKGROUND_MUSIC_VOLUME })
			}
			return
		}

		this.backgroundMusic = this.sound.add(MENU_BACKGROUND_MUSIC_KEY, {
			loop: true,
			volume: MENU_BACKGROUND_MUSIC_VOLUME
		})
		this.backgroundMusic.play()
	}

	private stopBackgroundMusic()
	{
		if (this.backgroundMusic)
		{
			this.backgroundMusic.stop()
			this.backgroundMusic.destroy()
			this.backgroundMusic = undefined
			return
		}

		const existingMusic = this.sound.get(MENU_BACKGROUND_MUSIC_KEY)
		if (existingMusic)
		{
			existingMusic.stop()
			existingMusic.destroy()
		}
	}

	private layout()
	{
		if (!this.background)
		{
			return
		}

		this.background.setPosition(0, 0)
		this.background.setDisplaySize(this.scale.width, this.scale.height)

		if (this.titleText)
		{
			this.titleText.setPosition(this.scale.width * 0.5, this.scale.height * 0.20)
		}

		if (this.statusText)
		{
			this.statusText.setPosition(this.scale.width * 0.5, this.scale.height * 0.65)
		}

		this.layoutRankingRows()
	}

	private layoutRankingRows()
	{
		if (!this.background)
		{
			return
		}

		const tableLeft = this.scale.width * TABLE_LEFT_RATIO
		const startY = this.scale.height * TABLE_TOP_RATIO
		const rowHeight = this.scale.height * ROW_HEIGHT_RATIO
		const rankCenterX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.10)
		const nameX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.29) - 20
		const scoreRightX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.95)
		const separatorX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.54)
		const separatorScaleX = this.scale.width / REFERENCE_BG_WIDTH
		const separatorScaleY = this.scale.height / REFERENCE_BG_HEIGHT
		const highlightCenterX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.5)
		const highlightScaleX = 637 //this.scale.width / REFERENCE_BG_WIDTH
		const highlightScaleY = 73 //this.scale.height / REFERENCE_BG_HEIGHT
        
		this.rowTexts.forEach((rowSet, index) => {
			const y = startY + (index * rowHeight)
			const isLastRow = index === (this.rowTexts.length - 1)
			const highlightPositionOffset = rowSet.row.isCurrentPlayer
				? this.getHighlightedPositionOffsetPx(rowSet.row.position)
				: 0
			if (rowSet.highlightBg)
			{
				rowSet.highlightBg
					.setPosition(highlightCenterX, y)
					//.setScale(highlightScaleX, highlightScaleY)
			}
			rowSet.positionText.setPosition(rankCenterX + highlightPositionOffset, y)
			rowSet.positionText.setText(this.formatPositionLabel(rowSet.row.position))
			rowSet.separatorImage
				.setPosition(separatorX - 22, y + SEPARATOR_Y_OFFSET)
				.setDisplaySize(
					RANKING_SEPARATOR_WIDTH,
					RANKING_SEPARATOR_HEIGHT
				)
				.setVisible(!rowSet.row.isCurrentPlayer && !isLastRow)
			rowSet.scoreText.setPosition(scoreRightX, y)
			rowSet.scoreText.setText(this.formatScore(rowSet.row.score))
			rowSet.nicknameText.setPosition(nameX, y)
			rowSet.nicknameText.setText(rowSet.row.nickname)
		})
	}

	private getHighlightedPositionOffsetPx(position: number): number
	{
		const digits = Math.abs(position).toString().length
		if (digits >= 4)
		{
			return 20
		}

		if (digits >= 3)
		{
			return 15
		}

		if (digits >= 2)
		{
			return 4
		}

		return 0
	}

	private formatScore(score: number): string
	{
		return Math.round(score).toLocaleString('es-ES')
	}

	private showRankingUnavailable(message: string)
	{
		this.rankingAvailable = false
		this.statusText?.setText(message)
		this.drawRows([])
		this.layout()
	}

	private getBackgroundTextureKey(): string
	{
		return this.endReason === 'game_over'
			? GAME_OVER_BACKGROUND_KEY
			: VICTORY_BACKGROUND_KEY
	}

	private formatPositionLabel(position: number): string
	{
		return position.toString() + '.'
	}

	private async finishRunAndLoadRanking()
	{
		if (!this.rankingApi)
		{
			this.showRankingUnavailable('Ranking no disponible\npor el momento')
			return
		}

		const session = getPersistentRankingRunSession()
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
		const today = this.getDateInTimezone(timezone)

		if (!session)
		{
			this.statusText?.setText('')
			await this.renderRankingRows(today, timezone, null, undefined)
			return
		}

		if (isLocalSessionId(session.runId))
		{
			if (!session.finished)
			{
				enqueueFinishOperation(session.runId, {
					seq_final: Math.max(1, session.seq),
					end_reason: this.endReason,
					client_ended_at_ms: Date.now(),
					final_budget: this.summary.finalBudget,
					final_max_budget: this.summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: this.summary.nickname,
						elapsed_ms: this.summary.elapsedMs
					}
				})
				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}

			this.showRankingUnavailable('Ranking no disponible\npor el momento')
			return
		}

		if (!session.finished)
		{
			try
			{
				await this.rankingApi.finishRun({
					run_id: session.runId,
					seq_final: Math.max(1, session.seq),
					end_reason: this.endReason,
					client_ended_at_ms: Date.now(),
					final_budget: this.summary.finalBudget,
					final_max_budget: this.summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: this.summary.nickname,
						elapsed_ms: this.summary.elapsedMs
					}
				}, session.runToken)

				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}
			catch (error)
			{
				// console.warn('[ranking-api] finish failed on ranking screen, queuing for later sync', error)
				enqueueFinishOperation(session.runId, {
					seq_final: Math.max(1, session.seq),
					end_reason: this.endReason,
					client_ended_at_ms: Date.now(),
					final_budget: this.summary.finalBudget,
					final_max_budget: this.summary.finalMaxBudget,
					tz: timezone,
					summary: {
						nickname: this.summary.nickname,
						elapsed_ms: this.summary.elapsedMs
					}
				}, session.runToken)
				setPersistentRankingRunSession({
					...session,
					finished: true
				})
			}
		}

		await this.renderRankingRows(today, timezone, session.runId, session)
	}

	private async renderRankingRows(
		date: string,
		timeZone: string,
		myRunId: string | null,
		session: ReturnType<typeof getPersistentRankingRunSession> | undefined
	)
	{
		try
		{
			const dailyRanking = await this.rankingApi!.getDailyRanking(date, timeZone, TOP_RANKING_LIMIT)
			let myPosition: number | null = null
			let myScore = this.summary.finalBudget
			const canIncludeCurrentPlayerRow = this.endReason === 'finished_level'

			if (myRunId && canIncludeCurrentPlayerRow)
			{
				try
				{
					const myRanking = await this.rankingApi!.getMyRanking(myRunId, timeZone)
					myPosition = myRanking.position
					myScore = myRanking.score
				}
				catch (error)
				{
					// console.warn('[ranking-api] could not load my ranking row', error)
				}
			}

			const rows = this.composeRows(
				dailyRanking.ranking,
				myPosition,
				myScore,
				canIncludeCurrentPlayerRow ? session?.runId : undefined
			)
			this.rankingAvailable = true
			this.drawRows(rows)
			this.statusText?.setText('')
			this.layout()
		}
		catch (error)
		{
			// console.warn('[ranking-api] daily ranking failed', error)
			this.showRankingUnavailable('Ranking no disponible\npor el momento')
		}
	}

	private composeRows(
		dailyRows: DailyRankingRow[],
		myPosition: number | null,
		myScore: number,
		myRunId?: string
	): RankingRowView[]
	{
		const baseRows: RankingRowView[] = dailyRows.map(row => ({
			position: row.position,
			nickname: row.nickname,
			score: row.score,
			isCurrentPlayer: Boolean(myRunId && row.run_id === myRunId)
		}))

		if (this.endReason === 'game_over')
		{
			return baseRows.slice(0, TOP_RANKING_LIMIT).map(row => ({
				...row,
				isCurrentPlayer: false
			}))
		}

		if (!myRunId || !myPosition || myPosition <= TOP_RANKING_LIMIT)
		{
			return baseRows.slice(0, TOP_RANKING_LIMIT)
		}

		const topFour = baseRows.slice(0, Math.max(0, TOP_RANKING_LIMIT - 1))
		return [
			...topFour,
			{
				position: myPosition,
				nickname: this.summary.nickname,
				score: myScore,
				isCurrentPlayer: true
			}
		]
	}

	private drawRows(rows: RankingRowView[])
	{
		this.rowTexts.forEach(rowSet => {
			rowSet.highlightBg?.destroy()
			rowSet.positionText.destroy()
			rowSet.separatorImage.destroy()
			rowSet.scoreText.destroy()
			rowSet.nicknameText.destroy()
		})
		this.rowTexts = []

		if (!this.background)
		{
			return
		}

		const tableLeft = this.scale.width * TABLE_LEFT_RATIO
		const startY = this.scale.height * TABLE_TOP_RATIO
		const rowHeight = this.scale.height * ROW_HEIGHT_RATIO
		const rankCenterX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.10)
		const nameX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.29)
		const scoreRightX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.95)
		const separatorX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.54)
		const separatorScaleX = this.scale.width / REFERENCE_BG_WIDTH
		const separatorScaleY = this.scale.height / REFERENCE_BG_HEIGHT
		const rowCenterX = tableLeft + (this.scale.width * TABLE_WIDTH_RATIO * 0.5)
        const HIGHLIGHT_ROW_SCALE_FACTOR = 0.8
		const highlightScaleX = 637 //this.scale.width / REFERENCE_BG_WIDTH
		const highlightScaleY = 73 //this.scale.height / REFERENCE_BG_HEIGHT

		rows.forEach((row, index) => {
			const y = startY + (index * rowHeight)
			const isLastRow = index === (rows.length - 1)
			const highlightPositionOffset = row.isCurrentPlayer
				? this.getHighlightedPositionOffsetPx(row.position)
				: 0
			const rowColor = row.isCurrentPlayer ? '#ffb22e' : '#0d183e'
			const highlightBg = row.isCurrentPlayer
				? this.add.image(rowCenterX, y, HIGHLIGHT_ROW_KEY)
					//.setScale(highlightScaleX, highlightScaleY)
					.setDepth(18)
				: undefined

			const positionText = this.add.text(rankCenterX + highlightPositionOffset, y, this.formatPositionLabel(row.position), {
				fontFamily: PIXEL_FONT_FAMILY,
				fontSize: `${RANKING_ROW_FONT_SIZE_PX}px`,
				color: rowColor
			})
				.setOrigin(0.5, 0.5)
				.setDepth(20)

			const separatorImage = this.add.image(separatorX, y + SEPARATOR_Y_OFFSET, RANKING_SEPARATOR_KEY)
				.setDisplaySize(
					RANKING_SEPARATOR_WIDTH,
					RANKING_SEPARATOR_HEIGHT
				)
				.setDepth(15)
				.setVisible(!row.isCurrentPlayer && !isLastRow)

			const scoreText = this.add.text(scoreRightX, y, this.formatScore(row.score), {
				fontFamily: PIXEL_FONT_FAMILY,
				fontSize: `${RANKING_ROW_FONT_SIZE_PX}px`,
				color: rowColor
			})
				.setOrigin(1, 0.5)
				.setDepth(20)

			const nicknameText = this.add.text(nameX, y, row.nickname, {
				fontFamily: PIXEL_FONT_FAMILY,
				fontSize: `${RANKING_ROW_FONT_SIZE_PX}px`,
				color: rowColor
			})
				.setOrigin(0, 0.5)
				.setDepth(20)

			this.rowTexts.push({
				row,
				highlightBg,
				positionText,
				separatorImage,
				scoreText,
				nicknameText
			})
		})
	}

	private restartGame()
	{
		clearPersistentRankingRunSession()
		clearPersistentBudgetState()
		clearPersistentPlayTimeState()
		setLogoOverlayVisible(false)
		this.scene.stop('logo-overlay')
		this.scene.start('intro')
	}

	private getDateInTimezone(timeZone: string): string
	{
		const formatter = new Intl.DateTimeFormat('en-CA', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		})

		return formatter.format(new Date())
	}
}
